import DateFnsUtils from "@date-io/date-fns";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import DeleteIcon from "@material-ui/icons/Delete";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import ClassNames from "classnames";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import Accordion from "../../components/Accordion";
import BackButton from "../../components/BackButton";
import Button from "../../components/Button";
import GentleReminderCard from "../../components/GentleReminderCard";
import NotesSidebar from "../../components/NotesSidebar";
import PageContent from "../../components/PageContent";
import SatisfactionRating from "../../components/SatisfactionRating";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import SessionBasicInfo from "../../components/SessionBasicInfo";
import SessionDeleteButton from "../../components/SessionDeleteButton";
import SessionSummary from "../../components/SessionSummary";
import Stepper from "../../components/Stepper";
import {
  DeleteSessionStatus,
  Framework,
  SessionStatus,
} from "../../config/Config";
import {
  IsCampaignManager,
  IsSessionCoach,
  IsSessionCoachee,
  IsSessionReadOnly,
} from "../../config/Roles";
import { useNotification } from "../../context/notification";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import GetSessionData from "../../services/getSessionData";
import GetReportingManagerForSession from "../../services/getSessionReportingManager";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import { dayJS } from "../../utils/datetime";
import styles from "./SessionsSetCheckpoints.module.scss";

const checkpointTemplate = {
  date: null,
  disabled: false,
};

export function SessionsSetCheckpoints() {
  const history = useHistory();
  const { addNotification } = useNotification();
  const { id } = useParams();
  const { t } = useTranslation();

  const [sessionDetails, setSessionDetails] = useState({});
  const [growFormDetails, setGrowFormDetails] = useState({});
  const [checkpoints, setCheckpoints] = useState([]);
  const [reportingManager, setReportingManager] = useState(null);
  const [sessionCoach, setSessionCoach] = useState(null);
  const [sessionCoachee, setSessionCoachee] = useState(null);
  const [isPendingCheckpoint, setIsPendingCheckpoint] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [checkpointFull, setCheckpointFull] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPDFlow, SetIsPDFlow] = useState(false);

  const intialForm = {
    rating: null,
    checkpoints: [],
    finalScores: [],
  };

  if (!localStorage.sessionCheckPointData) {
    localStorage.setItem("sessionCheckPointData", JSON.stringify(intialForm));
  }

  const localData =
    localStorage.sessionCheckPointData &&
      localStorage.sessionCheckPointData !== undefined
      ? JSON.parse(localStorage.sessionCheckPointData)
      : {};

  const [rating, setRating] = useState(localData?.rating);

  useEffect(() => {
    // get session details
    async function fetchSessionData() {
      setIsLoading(true);
      const sessionAPIData = await GetSessionData(id);
      const reportingManagerAPIData = await GetReportingManagerForSession(id);
      if (sessionAPIData) {
        setSessionDetails(sessionAPIData);
        // initialize checkpoint list with date = 1 day after review session
        if (sessionAPIData.session) {
          let firstCP = checkpointTemplate;
          firstCP.date = dayJS(sessionAPIData.session.session_datetime)
            .add(dayJS().utcOffset(), "minutes") // convert from UTC to local
            .add(1, "day");

          // set first cp to current date if it's found to be greater than 1 day after review session
          if (new Date() > new Date(firstCP.date)) {
            firstCP.date = new Date();
          }
          setCheckpoints([firstCP]);
        }
      } else {
        addNotification(
          `Could not get session details from id #${id}`,
          "error"
        );
      }
      if (reportingManagerAPIData) {
        setReportingManager(reportingManagerAPIData);
      }
      setIsLoading(false);
    }

    fetchSessionData();

    // get grow form details
    apiRequest
      .get(`/api/getGrowformDetails?session_id=${id}`)
      .then(response => {
        const data = response.data.data;
        setGrowFormDetails(data);
      })
      .catch(error => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    setSessionCoach(IsSessionCoach(getSessionValue("coach_id")));
    setSessionCoachee(IsSessionCoachee(getSessionValue("coachee_id")));
    SetIsPDFlow(getSessionValue("framework") === Framework.PD);
    setIsPendingCheckpoint(
      getSessionValue("status") === SessionStatus.PENDING_CHECKPOINTS
    );
  }, [sessionDetails]);

  const isReadOnly = useMemo(() => {
    if (_.isEmpty(sessionDetails) || !sessionDetails.session) return true;
    return IsSessionReadOnly(
      sessionDetails.session.coach_id,
      sessionDetails.session.coachee_id
    );
  }, [sessionDetails]);

  const handleCheckpointAdd = () => {
    if (checkpoints.length >= 5) {
      addNotification("You can only add a maximum of 4 checkpoints", "warning");
    } else {
      let newCheckpoints = _.cloneDeep(checkpoints);
      let newCP = checkpointTemplate;

      // making sure newly-added checkpoint's initial date is greater than its previous checkpoints' dates
      if (checkpoints.length >= 1) {
        newCP.date = checkpoints[checkpoints.length - 1].date;
        newCP.date = dayJS(newCP.date).add(1, "day");
      }
      newCheckpoints.push(newCP);
      setCheckpoints(newCheckpoints);
    }
  };

  const handleCheckpointDelete = index => {
    let newCheckpoints = _.cloneDeep(checkpoints);
    newCheckpoints.splice(index, 1);
    setCheckpoints(newCheckpoints);
  };

  const handleDateChange = (date, index) => {
    let newCheckpoints = _.cloneDeep(checkpoints);
    newCheckpoints[index].date = date;
    setCheckpoints(newCheckpoints);
  };

  // helper function to access values within session details
  const getSessionValue = key => {
    return sessionDetails.session ? sessionDetails.session[key] : undefined;
  };

  // helper function to access values within grow form
  const getGrowFormValue = key => {
    return growFormDetails.grow_form_static_data
      ? growFormDetails.grow_form_static_data[key]
      : undefined;
  };

  // get current session duration for the stepper
  const getTotalTime = time => {
    setSessionDuration(time);
  };

  // check if the last checkpoint item is already 1 day before to review date (the last valid date). if so, disable add checkpoint button.
  useEffect(() => {
    if (getSessionValue("review_datetime")) {
      let lastCheckpoint = new Date(
        dayJS(checkpoints[checkpoints.length - 1].date)
          .add(1, "day")
          .format("YYYY-MM-DD")
      );
      let reviewDate = new Date(
        dayJS(getSessionValue("review_datetime"))
          .add(dayJS().utcOffset(), "minutes") // convertfrom UTC to local timezone
          .format("YYYY-MM-DD")
      );

      if (lastCheckpoint >= reviewDate) {
        setCheckpointFull(true);
      } else {
        setCheckpointFull(false);
      }
    }
  }, [checkpoints]);

  const handleConfirm = () => {
    if (!!!rating | (parseInt(rating) < -1)) {
      addNotification(t("setCheckpoint.pleaseRateTheCoachees"), "warning");
      return;
    }
    const paramsObj = {
      session_id: parseInt(id),
      coachee_behaviour_rating: parseInt(rating),
      created_by: getSessionValue("session_created_by"),
      checkpoints: [],
      duration: sessionDuration,
    };

    paramsObj.checkpoints = checkpoints.map(cp => {
      return {
        checkpoint: dayJS(cp.date).format("YYYY-MM-DD"),
      };
    });

    //console.log("paramsObj", paramsObj);return;

    setIsLoading(true);
    apiRequest
      .post(`/api/confirmRatingCheckpoints`, paramsObj)
      .then(response => {
        if (response && response.status && response.status === 200) {
          history.push(`/sessions/update-checkpoints/${id}`); // goto next page in flow
        }
        setIsLoading(false);
      })
      .catch(error => {
        setIsLoading(false);
        console.error(error);
      });
  };

  const handlePDFlow = () => {
    if (!!!rating | (parseInt(rating) < -1)) {
      addNotification("Please rate the coachee's behavior.", "warning");
      return;
    }
    setIsLoading(true);
    const formObject = {
      session_id: parseInt(id),
      coachee_behaviour_rating: parseInt(rating),
      duration: sessionDuration,
      created_by: getSessionValue("session_created_by"),
    };

    apiRequest
      .post(`/api/coacheeBehaviorRating`, formObject)
      .then(response => {
        if (response && response.status && response.status === 200) {
          history.push(`/sessions/teams`); // Session end here, back to session list.
        }
        setIsLoading(false);
      })
      .catch(error => {
        setIsLoading(false);
        console.error(error);
      });
  };

  return (
    <DashboardLayout
      appBarContent={
        sessionDetails && (
          <Stepper
            isPDFlow={isPDFlow}
            phase={
              isPDFlow ? t("header.stepper.act") : t("header.stepper.check")
            }
            phaseLabel={isPDFlow ? "Act" : "Check"}
            sessionId={id}
            clockRun={sessionCoach}
            getTotalTimeSpent={getTotalTime}
          />
        )
      }
      rightSidebarContent={
        <NotesSidebar
          sessionID={id}
          isCoach={
            sessionDetails["coach_id"] === parseInt(getLocalValue("uid"))
          }
          accessible={!isReadOnly}
        />
      }
      contentClass={styles.dashboardLayout__content}
    >
      <div className={styles.pageWrapper}>
        <PageContent isPDFlow={isPDFlow} indent>
          <Section>
            <div className={styles.detailsContainer}>
              <BackButton text={t("common.backToSession")} />
              <SectionTitle
                append={
                  <>
                    {getLocalValue("employee_id") === reportingManager &&
                      IsCampaignManager() &&
                      DeleteSessionStatus.includes(
                        sessionDetails?.session?.status
                      ) && <SessionDeleteButton sessionId={id} />}
                  </>
                }
              >
                {isPDFlow
                  ? t("coachingSummary.coachingSummary")
                  : t("setCheckpoint.setCheckpoint")}
              </SectionTitle>
              <div className={styles.sessionDetailsBlock}>
                <SessionBasicInfo sessionData={sessionDetails.session} />
                <div>
                  <Accordion
                    summary={t("setCheckpoint.viewSessionSummary")}
                    details={<SessionSummary id={id}></SessionSummary>}
                  />
                </div>
                {sessionCoach && (
                  <div className={styles.ratingCard}>
                    <label className={styles.label}>
                      {t("setCheckpoint.rateCoacheesBehaviour")}
                    </label>
                    <SatisfactionRating
                      ratingValue={rating}
                      // coacheeRating={2}
                      onChange={val => {
                        // console.log("Rating::", val);
                        if (localStorage.sessionCheckPointData) {
                          const temp = JSON.parse(
                            localStorage.sessionCheckPointData
                          );
                          temp.rating = val;
                          localStorage.setItem(
                            "sessionCheckPointData",
                            JSON.stringify(temp)
                          );
                        }

                        setRating(val);
                      }}
                    />
                  </div>
                )}
                {/* <div className={styles.ratingCard}>
                  <StarRatingCard
                    value={rating}
                    setValue={setRating}
                    label={t("setCheckpoint.rateCoacheesBehaviour")}
                  />
                </div> */}
              </div>
            </div>
          </Section>
          {
            //If PD Flow Add checkpoint section will be hidden
            !isPDFlow && (
              <Section>
                <div className={styles.sessionCheckpointsBlock}>
                  <div className={styles.checkpointContainer}>
                    {checkpoints.map((item, index) => {
                      return (
                        <div
                          className={ClassNames(
                            styles.checkpointsRow,
                            styles.row
                          )}
                          key={index}
                        >
                          <div
                            className={ClassNames(
                              styles.rowLeft,
                              styles.checkpointsLabel
                            )}
                          >
                            {t("setCheckpoint.checkpoint")} {index + 1}
                          </div>
                          <div
                            className={styles.rowRight}
                            style={{ alignItems: "flex-start" }}
                          >
                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                              <AccessTimeIcon
                                className={styles.pickerIcon}
                                fontSize="inherit"
                                style={{ top: "15px" }}
                              />
                              <DatePicker
                                disableToolbar
                                variant="inline"
                                inputVariant="outlined"
                                disablePast={true}
                                minDate={
                                  index === 0
                                    ? dayJS(getSessionValue("session_datetime"))
                                      .add(dayJS().utcOffset(), "minutes") // convert from UTC To local timezone
                                      .add(1, "day") // first checkpoint must be at least 1 day after coaching date
                                    : dayJS(checkpoints[index - 1].date).add(
                                      1,
                                      "day"
                                    ) // no duplicate dates. cannot pick same day as the checkpoint item before.
                                }
                                autoOk
                                maxDate={
                                  checkpoints.length > index + 1
                                    ? dayJS(
                                      checkpoints[index + 1].date
                                    ).subtract(1, "day") // no duplicate dates. cannot exceed the next checkpoint item's date
                                    : dayJS(getSessionValue("review_datetime"))
                                      .add(dayJS().utcOffset(), "minutes") // convert from UTC To local timezone
                                      .subtract(1, "day")
                                }
                                className={styles.datePicker}
                                format="dd/M/yyyy"
                                onChange={e => handleDateChange(e, index)}
                                value={item.date}
                                animateYearScrolling
                                disabled={isReadOnly}
                              />
                            </MuiPickersUtilsProvider>
                            <div className={styles.actions}>
                              {checkpoints.length > 1 && !isReadOnly && (
                                <DeleteIcon
                                  onClick={() => handleCheckpointDelete(index)}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div
                      className={ClassNames(styles.checkpointsRow, styles.row)}
                    >
                      <div className={styles.rowLeft}></div>
                      <div className={styles.rowRight}>
                        <Button
                          className={styles.checkpointsAddBtn}
                          variant="outlined"
                          disabled={
                            (checkpoints.length >= 5) |
                            checkpointFull |
                            isReadOnly
                          }
                          onClick={handleCheckpointAdd}
                        >
                          {t("setCheckpoint.addCheckpoint")}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {getSessionValue("review_datetime") ? (
                    <div className={styles.row}>
                      <div className={styles.rowLeft}>
                        {t("setCheckpoint.reviewDate")}
                      </div>
                      <div className={styles.rowRight}>
                        <span className={styles.rowIcon}>
                          <CalendarTodayIcon />
                        </span>
                        <span style={{ padding: "0 12px 0 4px" }}>
                          {/* Time format: 9 Jan 2020 */}
                          {dayJS(getSessionValue("review_datetime"))
                            .add(dayJS().utcOffset(), "minutes") // convert from UTC To local timezone
                            .format("D MMM YYYY")}
                        </span>
                        <span className={styles.rowIcon}>
                          <ScheduleIcon />
                        </span>
                        <span style={{ padding: "0 12px 0 4px" }}>
                          {/* Time format: 8:12 PM*/}
                          {dayJS(getSessionValue("review_datetime"))
                            .add(dayJS().utcOffset(), "minutes") // convert from UTC To local timezone
                            .format("h:mm A")}
                        </span>
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              </Section>
            )
          }
        </PageContent>
        {sessionCoach && isPendingCheckpoint && !isPDFlow && !isReadOnly && (
          <PageContent indent>
            <Section>
              <div className={styles.detailsContainer}>
                <GentleReminderCard
                  label={t("sessionDetails.gentleReminderTitle")}
                  description={t("setCheckpoint.gentleReminder")}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={handleConfirm}
                  isloading={isLoading ? 1 : 0}
                >
                  {t("setCheckpoint.confirmRatingAndCheckpoints")}
                </Button>
              </div>
            </Section>
          </PageContent>
        )}
        {sessionCoach && isPDFlow && !isReadOnly && (
          <PageContent indent>
            <Section>
              <div className={styles.detailsContainer}>
                <GentleReminderCard
                  label={t("sessionDetails.gentleReminderTitle")}
                  description="You won’t be able to change your rating after you have confirmed."
                />
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handlePDFlow}
                  disabled={!sessionDuration || sessionDuration === 0}
                  isloading={isLoading ? 1 : 0}
                >
                  Confirm rating and end session
                </Button>
              </div>
            </Section>
          </PageContent>
        )}
      </div>
    </DashboardLayout>
  );
}
